/*
Translated copy for text the components write at runtime, from
window.themeLocalization (snippets/theme-localization.liquid).
*/

const themeString = (key, fallback) =>
  window.themeLocalization?.strings?.[key] || fallback;

const formatCountdown = (days, hours, minutes, seconds) =>
  themeString("countdownFormat", "{days}d {hours}h {minutes}m {seconds}s")
    .replace("{days}", days)
    .replace("{hours}", hours)
    .replace("{minutes}", minutes)
    .replace("{seconds}", seconds);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector(".menu-toggle");
    this.header = this.querySelector(".site-header");
    this.menu = this.querySelector(".menu");
    this.scroll = false;

    if (this.toggle) {
      this.toggle.addEventListener("click", (e) => {
        if (this.menu.classList.contains("active")) {
          this.closeMenu();
        } else {
          this.openMenu();
        }
      });
    }

    this.lastScrollTop = 0;

    // "Scrolled" means past 50px. Reading scrollTop on connect forced a
    // synchronous layout in the middle of page load (Lighthouse's forced reflow),
    // so a 1px marker sits 50px down the page and an IntersectionObserver
    // reports when it leaves the viewport. The browser computes that during its
    // own rendering step, and the first callback carries the initial state,
    // including a restored scroll position.
    this.scrollMarker = document.createElement("div");
    this.scrollMarker.setAttribute("aria-hidden", "true");
    this.scrollMarker.style.cssText =
      "position:absolute;top:50px;left:0;width:1px;height:1px;pointer-events:none;";
    document.body.prepend(this.scrollMarker);

    this.scrollObserver = new IntersectionObserver(([entry]) => {
      this.scroll = !entry.isIntersecting;
      this.header.classList.toggle("scrolled", this.scroll);
    });
    this.scrollObserver.observe(this.scrollMarker);
  }

  disconnectedCallback() {
    this.scrollObserver?.disconnect();
    this.scrollMarker?.remove();
  }

  openMenu() {
    this.menu.classList.add("active");
    this.toggle.classList.add("active");
    this.header.classList.add("active");
    if (this.toggle) {
      this.toggle.setAttribute("aria-expanded", "true");
      this.toggle.setAttribute("aria-label", themeString("closeMenu", "Close menu"));
    }
    document.querySelector("body").style.overflow = "hidden";
  }

  closeMenu() {
    this.menu.classList.remove("active");
    this.toggle.classList.remove("active");
    this.header.classList.remove("active");
    if (this.toggle) {
      this.toggle.setAttribute("aria-expanded", "false");
      this.toggle.setAttribute("aria-label", themeString("openMenu", "Open menu"));
    }
    document.querySelector("body").style.overflow = "auto";
  }
}

customElements.define("site-header", SiteHeader);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
    this.lastClickTime = 0;
    this.debounceDelay = 300; // 300ms debounce
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    } else {
      this.querySelector("button").setAttribute(
        "aria-label",
        themeString("previousSlide", "Previous slide")
      );
    }
    this.addEventListener("click", (e) => {
      const now = Date.now();
      if (now - this.lastClickTime < this.debounceDelay) {
        return;
      }
      this.lastClickTime = now;

      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
    this.lastClickTime = 0;
    this.debounceDelay = 300;
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    } else {
      this.querySelector("button").setAttribute(
        "aria-label",
        themeString("nextSlide", "Next slide")
      );
    }

    this.addEventListener("click", (e) => {
      const now = Date.now();
      if (now - this.lastClickTime < this.debounceDelay) {
        return;
      }
      this.lastClickTime = now;

      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    } else {
      pagerButtons[0].classList.add("active");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

/*
 * Swiper is only in the head on product templates. Everywhere else the first
 * carousel to connect fetches it, once, and every carousel waits on the same
 * promise, so pages without one never download it.
 */
let swiperLoading = null;

function loadSwiper() {
  if (window.Swiper) return Promise.resolve();
  if (swiperLoading) return swiperLoading;

  swiperLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = window.themeAssets?.swiperJs;
    script.onload = resolve;
    script.onerror = () => {
      swiperLoading = null;
      reject(new Error("Swiper failed to load"));
    };
    document.head.appendChild(script);
  });

  return swiperLoading;
}

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.lastNavigationTime = 0;
    this.navigationDebounceDelay = 300;
  }

  connectedCallback() {
    // Product templates load Swiper up front and their scripts read
    // carousel.swiper while initialising, so there it is built right away.
    if (window.Swiper) {
      this.init();
      return;
    }

    // Elsewhere a carousel is built once it comes within a viewport of the
    // screen, so the ones further down no longer download and lay out Swiper
    // while the page loads. One inside a hidden tab panel is built when its
    // panel opens, at its real width.
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.stopWatchingVisibility();
        loadSwiper()
          .then(() => {
            if (this.isConnected && !this.swiper) this.init();
          })
          .catch((error) => console.error(error));
      },
      { rootMargin: "100% 0px" }
    );
    this.visibilityObserver.observe(this);
  }

  stopWatchingVisibility() {
    this.visibilityObserver?.disconnect();
    this.visibilityObserver = null;
  }

  init() {
    if (this.swiper) return;

    const rawConfig = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    let config = { ...rawConfig };

    let swiperElement = null;
    const id = this.dataset.id;

    if (id) {
      const escapedId =
        window.CSS && typeof window.CSS.escape === "function"
          ? window.CSS.escape(id)
          : id;
      const scopedRoot = this.querySelector(`#${escapedId}`);
      if (scopedRoot) swiperElement = scopedRoot.querySelector(".swiper");
    }

    if (!swiperElement) swiperElement = this.querySelector(".swiper");
    const paginationElement = this.querySelector(".swiper-pagination");

    if (!swiperElement) {
      console.error(`Swiper element not found for carousel ${this.dataset.id}`);
      return;
    }

    this.swiper = new Swiper(swiperElement, {
      ...config,
      pagination: paginationElement
        ? {
            el: paginationElement,
            clickable: true,
            bulletClass: "swiper-pagination-bullet",
            bulletActiveClass: "swiper-pagination-bullet-active",
          }
        : false,
    });
    this.handleSlideChange();
    this.updateNavButtons();
    this.initializeThumbnailPagination();
    this.classList.remove("opacity-0");
  }

  disconnectedCallback() {
    this.stopWatchingVisibility();
    if (this.swiper && typeof this.swiper.destroy === "function") {
      this.swiper.destroy(true, true);
    }
    this.swiper = null;
  }

  previous() {
    const now = Date.now();
    if (now - this.lastNavigationTime < this.navigationDebounceDelay) {
      return;
    }
    this.lastNavigationTime = now;

    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    const now = Date.now();
    if (now - this.lastNavigationTime < this.navigationDebounceDelay) {
      return;
    }
    this.lastNavigationTime = now;

    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));
    const thumbnailPagination = this.querySelector(".thumbnail-pagination");

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          if (nextPager) nextPager.classList.add("active");
        });

        if (thumbnailPagination) {
          const thumbnailButtons = Array.from(
            thumbnailPagination.querySelectorAll(".thumbnail-btn")
          );
          thumbnailButtons.forEach((button) => {
            button.classList.remove("active");
          });

          const activeThumbnail = thumbnailButtons[this.swiper.realIndex];
          if (activeThumbnail) {
            activeThumbnail.classList.add("active");
            this.scrollThumbnailIntoView(thumbnailPagination, activeThumbnail);
          }
        }

        this.updateNavButtons();
      });
    }
  }

  // Centres the active thumbnail in a strip too long to fit. Only the strip
  // scrolls, so picking a colour lower down the page doesn't move the window.
  scrollThumbnailIntoView(strip, thumbnail) {
    const stripBox = strip.getBoundingClientRect();
    const thumbnailBox = thumbnail.getBoundingClientRect();

    strip.scrollBy({
      left:
        thumbnailBox.left -
        stripBox.left -
        (stripBox.width - thumbnailBox.width) / 2,
      behavior: "smooth",
    });
  }

  initializeThumbnailPagination() {
    const thumbnailPagination = this.querySelector(".thumbnail-pagination");
    if (!thumbnailPagination) return;

    const thumbnailButtons = Array.from(
      thumbnailPagination.querySelectorAll(".thumbnail-btn")
    );

    if (this.swiper && thumbnailButtons.length) {
      thumbnailButtons.forEach((btn) => btn.classList.remove("active"));
      const initialThumb =
        thumbnailButtons[this.swiper.realIndex] || thumbnailButtons[0];
      if (initialThumb) initialThumb.classList.add("active");
    } else if (thumbnailButtons.length) {
      thumbnailButtons[0].classList.add("active");
    }

    thumbnailButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        if (this.swiper) {
          this.swiper.slideTo(index);
        }
      });
    });
  }

  updateNavButtons() {
    const prevButtons = this.querySelectorAll("carousel-prev button");
    const nextButtons = this.querySelectorAll("carousel-next button");

    if (!this.swiper || this.swiper.params.loop) return;

    const isBeginning = this.swiper.isBeginning;
    const isEnd = this.swiper.isEnd;

    prevButtons.forEach((btn) => (btn.disabled = isBeginning));
    nextButtons.forEach((btn) => (btn.disabled = isEnd));
  }
}

customElements.define("site-carousel", SiteCarousel);

/* Site Video */
class SiteVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");
    this.playBtn = this.querySelector(".play-button-js");

    if (!this.video) return;

    this.pickVariant(); // <-- AHORA también inserta sources
    this.bindUI();

    // Fade out the eager LCP poster once the video actually starts playing
    const posterEl = this.querySelector("[data-video-poster]");
    if (posterEl) {
      this.video.addEventListener("playing", () =>
        posterEl.classList.add("opacity-0")
      );
    }

    if (this.dataset.stopOnObserve === "true") {
      this.stopOnObserve();
    } else {
      this.loadOnObserve();
    }
  }

  bindUI() {
    if (!this.playBtn) return;

    this.playBtn.addEventListener("click", () => {
      this.pauseOtherVideos();
      this.ensureHydrated(); // por si el usuario clickea antes de intersect
      this.video.play().catch(() => {});
    });

    this.video.addEventListener("click", () => {
      this.pauseOtherVideos();
      this.ensureHydrated();
      this.video.paused
        ? this.video.play().catch(() => {})
        : this.video.pause();
    });

    this.video.addEventListener("play", () =>
      this.playBtn.classList.add("hidden")
    );
    this.video.addEventListener("pause", () =>
      this.playBtn.classList.remove("hidden")
    );
    this.video.addEventListener("ended", () =>
      this.playBtn.classList.remove("hidden")
    );
  }

  pauseOtherVideos() {
    document.querySelectorAll("site-video video").forEach((v) => {
      if (v !== this.video && !v.paused) v.pause();
    });
  }

  pickVariant() {
    if (this.video.dataset.variantPicked === "1") return;

    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    const tpl = this.querySelector(
      isDesktop
        ? "template[data-sources-desktop]"
        : "template[data-sources-mobile]"
    );

    const poster = isDesktop
      ? this.video.dataset.posterDesktop
      : this.video.dataset.posterMobile;

    if (poster) this.video.setAttribute("poster", poster);

    // IMPORTANT: insertar sources UNA sola vez
    if (tpl && tpl.content) {
      // limpia por si el componente se re-monta (theme editor / ajax nav)
      this.video.querySelectorAll("source").forEach((s) => s.remove());
      this.video.appendChild(tpl.content.cloneNode(true));
    }

    this.video.dataset.variantPicked = "1";
  }

  ensureHydrated() {
    if (this.video.dataset.loaded === "1") return;

    this.video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute("data-src");
    });

    this.video.dataset.loaded = "1";
    this.video.load();
  }

  loadOnObserve() {
    if (!("IntersectionObserver" in window)) {
      // fallback: hidrata inmediato
      this.ensureHydrated();
      if (this.video.hasAttribute("autoplay"))
        this.video.play().catch(() => {});
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          this.ensureHydrated();
          obs.unobserve(entry.target);

          if (this.video.hasAttribute("autoplay")) {
            this.video.play().catch(() => {});
          }
        });
      },
      { rootMargin: "200px 0px", threshold: 0.01 }
    );

    observer.observe(this.video);
  }

  stopOnObserve() {
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) this.video.pause();
      });
    });

    observer.observe(this.video);
  }
}

customElements.define("site-video", SiteVideo);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.content = null;
  }

  connectedCallback() {
    this.icon = this.querySelector("svg");
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.querySelector(".content");
    if (!this.details || !this.content) return;

    this.initializeOpenState();
    // Only intercept clicks on the summary so links inside the content work normally
    if (this.summary) {
      this.summary.addEventListener("click", (e) => {
        e.preventDefault();
        this.animateHeight();
      });
    }
  }

  hasTruthyDataAttr(el, key) {
    if (!el || !el.dataset) return false;
    const v = el.dataset[key];
    return v === "" || v === "true" || v === "1";
  }

  initializeOpenState() {
    // Supports: <accordion-item data-open> / data-open="true"
    // Also supports: <accordion-item data-default-open> / data-default-open="true"
    // (and the same attributes on the child <details> element).
    const shouldOpenByDefault =
      this.hasTruthyDataAttr(this, "open") ||
      this.hasTruthyDataAttr(this, "defaultOpen") ||
      this.hasTruthyDataAttr(this.details, "open") ||
      this.hasTruthyDataAttr(this.details, "defaultOpen");

    if (shouldOpenByDefault) this.details.open = true;

    // Only the icon needs setting up: details[open] > .content already drops the
    // max-height cap in CSS. Writing a measured height here is what used to
    // break a section that starts open — inside a drawer that is off screen at
    // load, scrollHeight can come back as 0, and nothing measures it again.
    requestAnimationFrame(() => {
      if (!this.details || !this.content) return;
      if (this.details.open) {
        if (this.icon) this.icon.style.transform = "rotate(180deg)";
        this.content.style.maxHeight = "";
      } else {
        if (this.icon) this.icon.style.transform = "rotate(0deg)";
        this.content.style.maxHeight = "0px";
      }
    });
  }

  animateHeight() {
    if (this.details.open) {
      if (this.icon) {
        this.icon.style.transform = "rotate(0deg)";
      }
      // A section that has never been animated sits at max-height: none, and
      // none does not transition to a length. Pin it to its real height first,
      // then collapse on the next frame so the transition has somewhere to run
      // from.
      this.content.style.maxHeight = this.content.scrollHeight + "px";
      requestAnimationFrame(() => {
        this.content.style.maxHeight = "0px";
      });
      setTimeout(() => {
        this.details.open = false;
      }, 300);
    } else {
      if (this.icon) {
        this.icon.style.transform = "rotate(180deg)";
      }
      this.details.open = true;
      this.content.style.maxHeight = this.content.scrollHeight + "px";
    }
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const form = this.querySelector("form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const submitter =
        e.submitter ||
        form.querySelector(
          'button[type="submit"], button:not([type]), input[type="submit"]',
        );
      const $id = form.querySelector('[name="id"]');
      const $qty = form.querySelector('[name="quantity"]');
      const $sellingPlan = form.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;

      // Get selling plan value and trim whitespace
      const sellingPlanValue =
        $sellingPlan && $sellingPlan.value ? $sellingPlan.value.trim() : "";

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          const properties = {};
          form
            .querySelectorAll('input[name^="properties["]')
            .forEach((input) => {
              const match = input.name.match(/^properties\[(.+)\]$/);
              if (match && input.value !== "") properties[match[1]] = input.value;
            });

          const options = {};
          if (sellingPlanValue && sellingPlanValue !== "")
            options.selling_plan = sellingPlanValue;
          if (Object.keys(properties).length > 0)
            options.properties = properties;

          await cart.addVariantToCart($id.value, qty, options, {
            cartAdd: this,
            submitter,
          });
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Update Quantity
*/

class UpdateQuantity extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {}
}

customElements.define("update-quantity", UpdateQuantity);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;
      const itemEl = this.closest("cart-item") || e.target.closest("cart-item");
      const isLocked =
        itemEl &&
        itemEl.dataset &&
        (itemEl.dataset.isLocked === "true" ||
          itemEl.dataset.isReward === "true" ||
          itemEl.dataset.isMultiplier === "true");
      if (isLocked) return;
      const lineKey =
        itemEl && itemEl.dataset ? itemEl.dataset.lineKey : itemEl?.getAttribute?.("data-line-key");

      if (cart) {
        await cart.updateQuantity(lineKey || idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/* Number Input */

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const itemEl = this.closest("cart-item");
    const isLocked =
      itemEl &&
      itemEl.dataset &&
      (itemEl.dataset.isLocked === "true" ||
        itemEl.dataset.isReward === "true" ||
        itemEl.dataset.isMultiplier === "true");
    if (isLocked) return;
    const previousValue = this.input.value;
    const button = event.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Cart Item
*/

class CartItem extends HTMLElement {
  connectedCallback() {
    this.isReward = this.dataset.isReward === "true";
    this.isMultiplier = this.dataset.isMultiplier === "true";
    this.isLocked =
      this.dataset.isLocked === "true" || this.isReward || this.isMultiplier;
    this.isMembership = false;
    this.optionSelectorsSelectEls = Array.from(
      this.querySelectorAll(".option-selectors select")
    );
    this.quantityInputEl = this.querySelector("number-input input");
    this.removeButtonEl = this.querySelector(".remove-button-js");
    this.upgradeButtonEl = this.querySelector(".upgrade-to-sub-js");
    this.product = JSON.parse(this.dataset.product);
    this.selectedVariant = JSON.parse(this.dataset.selectedVariant);
    this.sellingPlanId = this.dataset.sellingPlanId;
    this.optionsWithValues = JSON.parse(this.dataset.productOptionsWithValues);
    this.cartIndex = this.dataset.cartIndex;
    this.lineKey = this.dataset.lineKey || this.getAttribute("data-line-key");

    if (!this.isLocked) {
      this.hideUnavailableOptions();
      this.addOptionChangeEvents();
      this.addQuantityChangeEvent();
    }

    this.addRemoveEvent();
    this.addUpgradeEvent();
  }

  hideUnavailableOptions() {
    this.optionSelectorsSelectEls.forEach((select) => {
      const options = Array.from(select.querySelectorAll("option"));
      options.forEach((option) => {
        const predictedOption = CartItem.predictVariantOption(
          this.product.variants,
          this.optionsWithValues,
          this.selectedVariant,
          option.dataset.optionName,
          option.value
        );

        if (!predictedOption.available) {
          option.setAttribute("disabled", "");
        }
      });
    });
  }

  addOptionChangeEvents() {
    this.optionSelectorsSelectEls.forEach((select) => {
      select.addEventListener("change", () => {
        const siteCart = document.querySelector("site-cart");
        const selectedOptions = this.optionSelectorsSelectEls.map(
          (select) => select.value
        );
        const newVariant = CartItem.findVariantByOptions(
          this.product.variants,
          selectedOptions
        );
        const quantityVal = this.quantityInputEl
          ? +this.quantityInputEl.value
          : 1;
        siteCart.updateCart({
          [this.selectedVariant.id]: 0,
          [newVariant.id]: quantityVal,
        });
      });
    });
  }

  addQuantityChangeEvent() {
    if (!this.quantityInputEl) return;
    this.quantityInputEl.addEventListener("change", () => {
      const isLocked =
        this.dataset.isLocked === "true" ||
        this.dataset.isReward === "true" ||
        this.dataset.isMultiplier === "true";
      if (isLocked) return;

      const siteCart = document.querySelector("site-cart");
      const quantityVal = +this.quantityInputEl.value;
      const item = this.quantityInputEl.closest("cart-item");
      const lineKey = item?.dataset?.lineKey || item?.getAttribute?.("data-line-key");
      siteCart.updateQuantity(lineKey || this.cartIndex, quantityVal);
    });
  }

  addRemoveEvent() {
    if (this.isLocked) return;
    if (!this.removeButtonEl) return;
    // Removal is handled by the <cart-remove> web component wrapper in this theme.
    // Avoid double-firing cart mutations which can race and/or be blocked by validations.
    if (this.removeButtonEl.closest("cart-remove")) return;
    this.removeButtonEl.addEventListener("click", () => {
      const siteCart = document.querySelector("site-cart");
      siteCart.updateCart({ [this.selectedVariant.id]: 0 });
    });
  }

  addUpgradeEvent() {
    if (this.upgradeButtonEl) {
      this.upgradeButtonEl.addEventListener("click", async (e) => {
        const siteCart = document.querySelector("site-cart");
        const item = e.target.closest("cart-item");
        const line = item.dataset.cartIndex;
        const sellingPlanId = e.target.dataset.sellingPlanId;
        const variantId = e.target.dataset.variantId;

        item
          .querySelector(".cart-item__totals")
          .firstElementChild.classList.add("hidden");
        item
          .querySelector(".cart-item__totals")
          .lastElementChild.classList.remove("hidden");

        await siteCart.upgradeToSubscription(line, 1, variantId, sellingPlanId);
      });
    }
  }

  static predictVariantOption(
    variants,
    optionsWithValues,
    selectedVariant,
    optionName,
    value
  ) {
    const predictedOptions = optionsWithValues.map((option) => {
      if (option.name === optionName) {
        return value;
      }

      return selectedVariant.options.find((opt) => option.values.includes(opt));
    });

    const predictedVariant = CartItem.findVariantByOptions(
      variants,
      predictedOptions
    );

    return {
      available: predictedVariant.available,
      priceDiff: predictedVariant.price - selectedVariant.price,
      variant: predictedVariant,
    };
  }

  static findVariantByOptions(variants, options) {
    const variant = variants.find((variant) => {
      const currentOptsStr = JSON.stringify(options.sort());
      const variantOptsStr = JSON.stringify(variant.options.sort());
      return currentOptsStr === variantOptsStr;
    });

    return variant;
  }

  upgradeToSubsctiption() {}
}

customElements.define("cart-item", CartItem);

/* Product Quantity Selector */

class ProductQuantitySelector extends HTMLElement {
  connectedCallback() {
    this.quantityResult = this.querySelector(".quantity-js");
    const quantityMinus = this.querySelector(".quantity-minus-js");
    const quantityPlus = this.querySelector(".quantity-plus-js");

    // Helper: robustly find the related cart-add element
    const getCartAdd = () => {
      const ancestor = this.closest("cart-add");
      if (ancestor) return ancestor;
      const mainProduct = this.closest("main-product");
      if (mainProduct) {
        const scoped = mainProduct.querySelector("cart-add");
        if (scoped) return scoped;
      }
      return document.querySelector("cart-add");
    };

    // Sync the hidden quantity input used when adding to cart
    const syncHiddenInput = () => {
      const cartAdd = getCartAdd();
      if (!cartAdd || !this.quantityResult) return;
      const quant = cartAdd.querySelector('[name="quantity"]');
      if (quant) {
        const val = parseInt(this.quantityResult.textContent, 10);
        quant.value = Number.isFinite(val) && val > 0 ? val : 1;
      }
    };

    if (quantityMinus) {
      quantityMinus.addEventListener("click", () => {
        const current = parseInt(this.quantityResult?.textContent, 10) || 1;
        const next = Math.max(1, current - 1);
        if (this.quantityResult) this.quantityResult.textContent = String(next);
        syncHiddenInput();
      });
    }

    if (quantityPlus) {
      quantityPlus.addEventListener("click", () => {
        const current = parseInt(this.quantityResult?.textContent, 10) || 1;
        const next = current + 1;
        if (this.quantityResult) this.quantityResult.textContent = String(next);
        syncHiddenInput();
      });
    }

    // Ensure initial sync on mount
    syncHiddenInput();
  }
}

customElements.define("product-quantity-selector", ProductQuantitySelector);

/* Product Item */

class ProductItem extends HTMLElement {
  connectedCallback() {
    const productData = this.dataset.product;
    if (!productData) return;

    this.product = JSON.parse(productData);
    this.selectedOptions = {};

    this.priceElement = this.querySelector(".product-price");
    this.variantIdInput = this.querySelector(".variant-id-input");
    this.variantButtons = this.querySelectorAll(".variant-btn");
    this.dropdownToggles = this.querySelectorAll(".variant-dropdown-toggle");

    const firstVariant = this.product.variants?.[0];
    if (firstVariant) {
      this.selectedOptions["1"] = firstVariant.option1;
      this.selectedOptions["2"] = firstVariant.option2;
      this.selectedOptions["3"] = firstVariant.option3;
    }

    this.variantButtons.forEach((btn) => {
      const position = btn.dataset.optionPosition;
      const value = btn.dataset.value;

      if (String(this.selectedOptions[position]) === String(value)) {
        btn.classList.add("active");
      }

      btn.addEventListener("click", () => {
        this.handleVariantChange(position, value, btn);
      });
    });

    this.dropdownToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        this.toggleDropdown(toggle);
      });
    });

    document.addEventListener("click", (e) => {
      if (
        !e.target.closest(".variant-dropdown") &&
        !e.target.closest(".variant-dropdown-toggle")
      ) {
        this.closeAllDropdowns();
      }
    });

    if (firstVariant) {
      this.updateProduct(firstVariant);
    }
  }

  handleVariantChange(position, value, clickedBtn) {
    this.selectedOptions[position] = value;

    this.variantButtons.forEach((btn) => {
      if (btn.dataset.optionPosition === position) {
        btn.classList.remove("active");
      }
    });

    clickedBtn.classList.add("active");

    const selectedVariant = this.product.variants.find((variant) => {
      return (
        variant.option1 === this.selectedOptions["1"] &&
        (variant.option2 === this.selectedOptions["2"] ||
          !this.selectedOptions["2"]) &&
        (variant.option3 === this.selectedOptions["3"] ||
          !this.selectedOptions["3"])
      );
    });

    if (selectedVariant) {
      this.updateProduct(selectedVariant);
    }
  }

  updateProduct(variant) {
    // The card image is never swapped for the variant's: it always shows the
    // product's first image, with the second one on hover (see product-item).
    if (this.priceElement) {
      const price = (variant.price / 100).toFixed(2).replace(/\.00$/, "");
      this.priceElement.textContent = `$${price}`;
    }

    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    const cartAdd = this.querySelector("cart-add");
    if (cartAdd) {
      cartAdd.dataset.variantId = variant.id;
    }

    const selectedColorText = this.querySelector(".selected-color-text");
    if (selectedColorText && variant.option1) {
      selectedColorText.textContent = variant.option1.toUpperCase();
    }

    this.updateDropdownSelections(variant);
  }

  toggleDropdown(toggle) {
    const dropdown = toggle.nextElementSibling;
    const isOpen = !dropdown.classList.contains("hidden");

    this.closeAllDropdowns();

    if (!isOpen) {
      dropdown.classList.remove("hidden");
      toggle.querySelector("svg").style.transform = "rotate(180deg)";
    }
  }

  closeAllDropdowns() {
    this.dropdownToggles.forEach((toggle) => {
      const dropdown = toggle.nextElementSibling;
      dropdown.classList.add("hidden");
      toggle.querySelector("svg").style.transform = "rotate(0deg)";
    });
  }

  updateDropdownSelections(variant) {
    this.dropdownToggles.forEach((toggle) => {
      const position = toggle.dataset.optionPosition;
      const selectedText = toggle.querySelector(".selected-option-text");

      if (selectedText) {
        const selectedValue = variant[`option${position}`];
        if (selectedValue) {
          selectedText.textContent = selectedValue;
        }
      }
    });
  }
}

customElements.define("product-item", ProductItem);

/*
Site Image
*/

class SiteImage extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (image) {
          const $image = image.target;
          if (image.isIntersecting) {
            $image.classList.add("active");
            imageObserver.unobserve($image);
          }
        });
      });

      imageObserver.observe(this.querySelector(".mediabox"));
    }
  }
}

customElements.define("site-image", SiteImage);

/*
Collection Switcher
*/

class CollectionSwitcher extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll(".switcher-tab"));
    this.panels = Array.from(this.querySelectorAll(".switcher-panel"));
    this.ctaWrapper = this.querySelector("[data-switcher-cta]");
    this.firstUrl = this.dataset.firstUrl;
    this.secondUrl = this.dataset.secondUrl;

    const initial = this.dataset.initial || "first";
    this.switchTo(initial);

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const key = tab.dataset.collectionKey;
        if (key) this.switchTo(key);
      });
    });
  }

  switchTo(key) {
    // Toggle active tab styles
    this.tabs.forEach((tab) => {
      const isActive = tab.dataset.collectionKey === key;
      tab.classList.toggle("active", isActive);
    });

    // Toggle visible panel
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.collectionKey === key;
      panel.classList.toggle("hidden", !isActive);
    });

    // If a <site-carousel> was initialized while hidden, it may need a refresh
    // after its panel becomes visible (Swiper width/height calculations).
    const activePanels = this.panels.filter(
      (panel) => panel.dataset.collectionKey === key
    );
    if (activePanels.length) {
      requestAnimationFrame(() => {
        activePanels.forEach((panel) => {
          panel.querySelectorAll("site-carousel").forEach((carousel) => {
            if (carousel && carousel.offsetParent === null) return;
            const swiper = carousel?.swiper;
            if (!swiper) return;
            swiper.update?.();
            swiper.updateSize?.();
            swiper.updateSlides?.();
          });
        });
      });
    }

    this.updateCta(key);
  }

  updateCta(key) {
    if (!this.ctaWrapper) return;
    const anchor = this.ctaWrapper.querySelector("a");
    if (!anchor) return;
    const url = key === "second" ? this.secondUrl : this.firstUrl;
    if (url) anchor.href = url;
  }
}

customElements.define("collection-switcher", CollectionSwitcher);

/* Quick Add Modal */

class QuickAddModal extends HTMLElement {
  constructor() {
    super();
    this.panel = null;
    this.content = null;
    this.veil = null;
    this.closeBtn = null;
    this.isOpen = false;
    this.cache = new Map();
    this._onDocClick = null;
    this._onKeyDown = null;
    this._onOutsideClick = null;
  }

  connectedCallback() {
    this.panel = this.querySelector(".modal-panel");
    this.content = this.querySelector(".modal-content");
    this.veil = this.querySelector(".modal-veil");
    this.closeBtn = this.querySelector(".modal-close");

    if (this.veil) this.veil.addEventListener("click", () => this.close());
    if (this.closeBtn)
      this.closeBtn.addEventListener("click", () => this.close());

    // Close modal when the add-to-cart form inside the modal is submitted
    this.addEventListener(
      "submit",
      (e) => {
        const form = e.target;
        if (form && form.closest && form.closest("cart-add")) {
          this.close();
        }
      },
      true
    );

    this._onKeyDown = (e) => {
      if (this.isOpen && (e.key === "Escape" || e.key === "Esc")) {
        this.close();
      }
    };
    document.addEventListener("keydown", this._onKeyDown);

    // Close when clicking outside the panel (e.g., on overlay wrapper)
    this._onOutsideClick = (e) => {
      if (!this.isOpen || !this.panel) return;
      const clickedInsidePanel = this.panel.contains(e.target);
      if (!clickedInsidePanel) {
        this.close();
      }
    };
    this.addEventListener("click", this._onOutsideClick);

    const root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : "/";

    this._onDocClick = async (e) => {
      const btn = e.target.closest("[data-quick-add]");
      if (!btn) return;
      e.preventDefault();
      const handle = btn.getAttribute("data-product-handle");
      if (!handle) return;

      const url = `${root}products/${handle}?view=quick-add`;
      try {
        let html = this.cache.get(handle);
        if (!html) {
          const resp = await fetch(url, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!resp.ok) throw new Error("Quick add fetch failed");
          html = await resp.text();
          this.cache.set(handle, html);
        }
        this.openWithHtml(html);
      } catch (err) {
        window.location.href = `${root}products/${handle}`;
      }
    };
    document.addEventListener("click", this._onDocClick);
  }

  disconnectedCallback() {
    if (this._onDocClick) {
      document.removeEventListener("click", this._onDocClick);
      this._onDocClick = null;
    }
    if (this._onKeyDown) {
      document.removeEventListener("keydown", this._onKeyDown);
      this._onKeyDown = null;
    }
    if (this._onOutsideClick) {
      this.removeEventListener("click", this._onOutsideClick);
      this._onOutsideClick = null;
    }
  }

  openWithHtml(html) {
    if (this.content) this.content.innerHTML = html;
    this.show();
  }

  show() {
    this.classList.remove("invisible");
    this.classList.remove("opacity-0");
    if (this.panel) {
      requestAnimationFrame(() => {
        this.panel.classList.remove("translate-y-4");
        this.panel.classList.remove("opacity-0");
      });
    }
    document.body.style.overflow = "hidden";
    this.isOpen = true;
  }

  close() {
    if (this.panel) {
      this.panel.classList.add("translate-y-4");
      this.panel.classList.add("opacity-0");
    }
    this.classList.add("opacity-0");
    setTimeout(() => {
      this.classList.add("invisible");
      if (this.content) this.content.innerHTML = "";
      document.body.removeAttribute("style");
    }, 200);
    this.isOpen = false;
  }
}

customElements.define("quick-add-modal", QuickAddModal);

/*
 * Product Recommendations Web Component
 * Fetches Shopify product recommendations for a product and injects them into a target container.
 *
 * Usage example in a section:
 * <product-recommendations
 *   target-id="product-recommendations-SECTION_ID"
 *   section-id="SECTION_ID"
 *   product-id="PRODUCT_ID"
 *   limit="8"
 *   intent="related">
 * </product-recommendations>
 */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === "true") return;

    const targetId = this.getAttribute("target-id");
    const sectionId = this.getAttribute("section-id");
    const productId = this.getAttribute("product-id");
    const limit = this.getAttribute("limit") || "8";
    const intent = this.getAttribute("intent") || "related";

    const container = targetId ? document.getElementById(targetId) : this;
    if (!container || !sectionId || !productId) return;

    const sectionEl =
      this.closest("[data-section]") ||
      document.querySelector(`[data-section-id="${sectionId}"]`);
    const root = (
      window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/"
    ).replace(/\/$/, "/");
    const url = `${root}recommendations/products?section_id=${encodeURIComponent(
      sectionId
    )}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(
      limit
    )}&intent=${encodeURIComponent(intent)}`;

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const updated =
          (targetId && doc.getElementById(targetId)) ||
          doc.querySelector(
            `#product-recommendations-${CSS.escape(sectionId)}`
          );

        if (updated && updated.innerHTML.trim().length) {
          container.innerHTML = updated.innerHTML;
          this.dataset.loaded = "true";
          container.dataset.loaded = "true";
          if (sectionEl) sectionEl.hidden = false;
          document.dispatchEvent(
            new CustomEvent("section:loaded", { detail: { id: sectionId } })
          );
        } else {
          if (sectionEl) sectionEl.style.display = "none";
        }
      })
      .catch(() => {
        if (sectionEl) sectionEl.style.display = "none";
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

/* Fade On View */

class FadeOnView extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.revealed = false;
  }

  // Nothing is measured on connect: reading each element's position there forced
  // a synchronous layout per element while the page loaded. The observer's first
  // callback carries the initial state instead. An element already on screen is
  // revealed without ever being hidden, so it does not fade on load; one below
  // the fold is hidden then, out of sight, and fades in when it scrolls into view.
  // It reveals as soon as any part of it is visible, which is what it always did
  // (the threshold attribute never changed that).
  connectedCallback() {
    if (!("IntersectionObserver" in window)) {
      this.reveal();
      return;
    }

    const rootMargin = this.getAttribute("root-margin") || "0px";

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.revealed) return;
        if (entries.some((entry) => entry.isIntersecting)) {
          this.reveal();
        } else {
          this.classList.add("fov-init");
        }
      },
      { threshold: 0, rootMargin }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  reveal() {
    this.revealed = true;
    this.classList.add("fov-show");
    this.classList.remove("fov-init");
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

customElements.define("fade-on-view", FadeOnView);

/* Countdown Timer — theme date/time settings are interpreted as Pacific (America/Los_Angeles). */

const COUNTDOWN_TIMEZONE = "America/Los_Angeles";

/**
 * @param {number} year
 * @param {number} month 1–12
 * @param {number} day
 * @param {number} hour24 0–23
 * @param {number} minute
 * @returns {Date}
 */
function wallTimeToDateInCountdownTimezone(year, month, day, hour24, minute) {
  let utcMillis = Date.UTC(year, month - 1, day, hour24, minute, 0);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: COUNTDOWN_TIMEZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  });
  for (let i = 0; i < 100; i += 1) {
    const parts = formatter.formatToParts(new Date(utcMillis));
    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    const y = get("year");
    const mon = get("month");
    const d = get("day");
    const h = get("hour");
    const min = get("minute");
    if (
      y === year &&
      mon === month &&
      d === day &&
      h === hour24 &&
      min === minute
    ) {
      return new Date(utcMillis);
    }
    const diff =
      Date.UTC(year, month - 1, day, hour24, minute) -
      Date.UTC(y, mon - 1, d, h, min);
    utcMillis += diff;
  }
  return new Date(utcMillis);
}

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const dateString = this.dataset.date;
    const hour12 = parseInt(this.dataset.hour || "0", 10);
    const ampm = (this.dataset.ampm || "AM").toUpperCase();

    let hour24 = hour12 % 12;
    if (ampm === "PM") hour24 += 12;

    const minute = parseInt(this.dataset.minute || "0", 10);
    const [year, month, day] = dateString.split("-").map(Number);
    this.endDate = wallTimeToDateInCountdownTimezone(
      year,
      month,
      day,
      hour24,
      minute
    );

    this.textTarget = this.querySelector("[data-countdown-text]") || this;

    this.updateCountdown();

    this.intrval = setInterval(() => this.updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intrval) clearInterval(this.intrval);
  }

  updateCountdown() {
    const now = new Date();
    const diff = this.endDate - now;

    if (diff <= 0) {
      this.textTarget.textContent = themeString(
        "countdownEnded",
        "Countdown has ended"
      );
      if (this.intrval) clearInterval(this.intrval);
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.textTarget.textContent = formatCountdown(days, hours, minutes, seconds);
  }
}

customElements.define("countdown-timer", CountdownTimer);

/* Resetting Countdown Timer */

class ResettingCountdownTimer extends HTMLElement {
  connectedCallback() {
    const startDateString = this.dataset.startDate;
    const intervalDays = parseInt(this.dataset.intervalDays || "7", 10);
    const hour12 = parseInt(this.dataset.hour || "0", 10);
    const ampm = (this.dataset.ampm || "AM").toUpperCase();

    if (!startDateString) {
      console.warn("ResettingCountdownTimer: startDate is required");
      return;
    }

    let hour24 = hour12 % 12;
    if (ampm === "PM") hour24 += 12;

    const minute = parseInt(this.dataset.minute || "0", 10);
    const [year, month, day] = startDateString.split("-").map(Number);
    this.startDate = wallTimeToDateInCountdownTimezone(
      year,
      month,
      day,
      hour24,
      minute
    );
    this.intervalDays = intervalDays;

    this.textTarget = this.querySelector("[data-countdown-text]") || this;

    this.calculateNextEndDate();
    this.updateCountdown();

    this.intrval = setInterval(() => this.updateCountdown(), 1000);
  }

  disconnectedCallback() {
    if (this.intrval) clearInterval(this.intrval);
  }

  calculateNextEndDate() {
    const nowMs = Date.now();
    const startMs = this.startDate.getTime();
    const periodMs = this.intervalDays * 24 * 60 * 60 * 1000;
    if (nowMs < startMs) {
      this.endDate = new Date(startMs);
      return;
    }
    const timeSinceStart = nowMs - startMs;
    const intervalsPassed = Math.floor(timeSinceStart / periodMs);
    let nextEndMs = startMs + (intervalsPassed + 1) * periodMs;
    while (nextEndMs <= nowMs) {
      nextEndMs += periodMs;
    }
    this.endDate = new Date(nextEndMs);
  }

  updateCountdown() {
    const now = new Date();
    const diff = this.endDate - now;

    if (diff <= 0) {
      // Countdown reached zero, reset to next interval
      this.calculateNextEndDate();
      this.updateCountdown();
      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.textTarget.textContent = formatCountdown(days, hours, minutes, seconds);
  }
}

customElements.define("resetting-countdown-timer", ResettingCountdownTimer);

/*
 * Sticky Countdown
 */

class StickyCountdown extends HTMLElement {
  constructor() {
    super();
    this.toggle = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.toggle = this.querySelector(".sticky-countdown-toggle");

    const savedState = localStorage.getItem("sticky-countdown-state");
    this.isOpen = savedState ? savedState === "open" : true;

    this.classList.toggle("active", this.isOpen);
    this.updateAria();

    this.toggle?.addEventListener("click", () => this.toggleState());
  }

  toggleState() {
    this.isOpen = !this.isOpen;
    this.classList.toggle("active", this.isOpen);

    localStorage.setItem(
      "sticky-countdown-state",
      this.isOpen ? "open" : "closed"
    );

    this.updateAria();
  }

  updateAria() {
    if (!this.toggle) return;
    this.toggle.setAttribute(
      "aria-label",
      this.isOpen
        ? themeString("hideCountdown", "Hide countdown")
        : themeString("showCountdown", "Show countdown")
    );
  }
}

customElements.define("sticky-countdown", StickyCountdown);

/*
Smart Marquee  
*/

class SmartMarquee extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.classList.add("is-looping");

    const direction = this.getAttribute("direction");
    if (direction === "reverse") {
      this.setAttribute("data-direction", "reverse");
    }

    let resizeTimer;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.classList.add("is-looping");
      }, 50);
    });
  }
}

customElements.define("smart-marquee", SmartMarquee);

/*
Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  constructor() {
    super();
    this.context = this.dataset.context;
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    if (this.form) {
      this.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.submitForm(e);
      });
    }
  }

  async submitForm(e) {
    const error = this.querySelector(".error");
    const success = this.querySelector(".success");
    const email = this.form.querySelector('[type="text"]');
    const submit = this.form.querySelector('[type="submit"]');
    const listId = this.dataset.listId;

    error.innerHTML = "";

    const submitText = submit.innerHTML;
    const submitWidth = submit.getBoundingClientRect().width;
    submit.style.width = `${submitWidth}px`;
    submit.innerHTML = "...";

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email.value,
                subscriptions: {
                  email: { marketing: { consent: "SUBSCRIBED" } },
                },
              },
            },
          },
        },
        ...(listId && {
          relationships: { list: { data: { type: "list", id: listId } } },
        }),
      },
    };

    try {
      const resp = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=RmunZE`,
        {
          method: "POST",
          headers: {
            Accept: "application/vnd.api+json",
            "Content-Type": "application/vnd.api+json",
            revision: "2025-01-15",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(
          errorData.errors?.[0]?.detail || "Klaviyo subscription request failed"
        );
      }

      this.form.reset();
      submit.innerHTML = submitText;
      success.classList.remove("hidden");
      email.blur();
    } catch (e) {
      error.classList.remove("hidden");
      console.error("Klaviyo signup failed:", e);
      error.textContent = themeString(
        "newsletterError",
        "Oops, there was an error. Please try again."
      );
      submit.innerHTML = submitText;
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);
